import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill, ReactQuillProps } from "react-quill";
import { EditorToolbar, formats, modules } from "./EditorToolbar";
import { LinkTooltip, TOOLTIP_WIDTH } from "./LinkTooltip";
import styled from "styled-components";
import { useMobileMediaQuery } from "hooks";
import "react-quill/dist/quill.snow.css";

interface QuillEditorProps extends ReactQuillProps {
  linkColor?: string;
  showHeading?: boolean;
}

export const QuillEditor: React.FC<QuillEditorProps> = ({
  linkColor,
  showHeading,
  ...props
}) => {
  const [quill, setQuill] = useState<ReactQuill | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [linkNode, setLinkNode] = useState<HTMLAnchorElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useMobileMediaQuery();

  const handlers = useMemo(
    () => ({
      link: function () {
        const range = this.quill.getSelection();
        if (!range || range.length === 0) return;
        const formats = this.quill.getFormat(range);
        const [leaf] = this.quill.getLeaf(range.index);
        const node = leaf.parent.domNode;
        setLinkNode(node);
        setUrl(formats.link || "");
      },
    }),
    [],
  );

  const handleLinkSave = (url: string) => {
    if (!quill) return;
    const editor = quill.getEditor();
    editor.format("link", url);
    const range = editor.getSelection();
    const [leaf] = editor.getLeaf(range.index);
    const node = leaf.parent.domNode;
    setLinkNode(node);
    setUrl(null);
  };

  const handleLinkRemove = () => {
    if (!quill) return;
    const editor = quill.getEditor();
    editor.format("link", false);
    setUrl(null);
  };

  const handleLinkClose = () => setUrl(null);

  useEffect(() => {
    if (!quill) return;
    const editor = quill.getEditor();

    const handleClick = (e) => {
      const node = e.target;
      if (node && (node.tagName === "A" || node.closest("a"))) {
        e.preventDefault();
        setLinkNode(node);

        const blot = Quill.find(e.target);
        if (!blot) return;

        const index = editor.getIndex(blot);
        editor.setSelection(index, e.target.innerText.length, "user");
        const format = editor.getFormat();
        setUrl(format.link || "");
      }
    };

    editor.root.addEventListener("click", handleClick);
    return () => editor.root.removeEventListener("click", handleClick);
  }, [quill]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <Container>
      <EditorToolbar
        ref={toolbarRef}
        quill={quill}
        linkColor={linkColor}
        showHeading={showHeading}
      />
      {isMounted && (
        <ReactQuill
          ref={setQuill}
          className="quill-html"
          {...props}
          modules={(() => {
            const mdls = props.modules || modules;
            return {
              ...mdls,
              toolbar: {
                ...mdls.toolbar,
                handlers,
              },
            };
          })()}
          formats={props.formats || formats}
          placeholder="Enter text..."
        />
      )}
      {url !== null && linkNode && toolbarRef.current && (
        <LinkTooltip
          url={url}
          onSave={handleLinkSave}
          onRemove={handleLinkRemove}
          onClose={handleLinkClose}
          style={{
            top:
              linkNode.offsetTop +
              toolbarRef.current.clientHeight +
              linkNode.getBoundingClientRect().height +
              5,
            left: isMobile
              ? 0
              : linkNode.offsetLeft -
                (TOOLTIP_WIDTH - linkNode.offsetWidth) / 2,
          }}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;

  .ql-container {
    border-radius: 0 0 7px 7px;
    min-height: 200px;
  }

  .ql-picker {
    font-family: "Poppins";
  }

  && {
    div.ql-toolbar button.ql-active .ql-stroke,
    div.ql-toolbar button:hover .ql-stroke {
      stroke: var(--text-color);
    }

    .ql-picker span.ql-picker-label {
      color: var(--text-color);

      .ql-stroke {
        stroke: var(--text-color);
      }
    }

    .ql-picker span.ql-picker-item {
      color: var(--text-color);
    }
  }
`;
